import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card,
  Form,
  Input,
  InputNumber,
  Select,
  Button,
  Row,
  Col,
  Divider,
  Descriptions,
  Space,
  Spin,
  Empty,
  Typography,
} from "antd";
import {
  SettingOutlined,
  ArrowLeftOutlined,
  SaveOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { getConfig, updateConfig } from "../network/api";
import PageHeader from "./common/PageHeader";
import { notify, serverError } from "../utils/notify";
import { relativeTime } from "../constants/health";
import dayjs from "dayjs";

const { Text } = Typography;

const ROTATIONS = [
  { value: 0, label: "0°" },
  { value: 90, label: "90°" },
  { value: 180, label: "180°" },
  { value: 270, label: "270°" },
];

// Editable fields only (metadata like _id/timestamps is shown read-only).
const EDITABLE_KEYS = [
  "title",
  "pictureFrequency",
  "uploadFrequency",
  "resolution",
  "rotation",
  "wifi",
  "externalUrl",
  "externalUrlHeaders",
];

const toForm = (cfg) => ({
  title: cfg.title ?? "",
  pictureFrequency: cfg.pictureFrequency ?? 600,
  uploadFrequency: cfg.uploadFrequency ?? 600,
  resolution: cfg.resolution ?? "",
  rotation: Number(cfg.rotation ?? 0),
  wifi: cfg.wifi ?? "",
  externalUrl: cfg.externalUrl ?? "",
  externalUrlHeaders: cfg.externalUrlHeaders ?? "",
});

const humanizeSeconds = (s) => {
  if (s === 0) return "event / door-trigger mode";
  if (!s) return "";
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.round((s / 60) * 10) / 10} min`;
  return `${Math.round((s / 3600) * 10) / 10} hr`;
};

const ConfigDetails = () => {
  const { configId } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();

  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const pictureFrequency = Form.useWatch("pictureFrequency", form);
  const uploadFrequency = Form.useWatch("uploadFrequency", form);

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getConfig(configId);
      if (response) {
        setConfig(response);
        form.setFieldsValue(toForm(response));
        setDirty(false);
      } else {
        setNotFound(true);
      }
    } catch (error) {
      if (error?.response?.status === 404) setNotFound(true);
      else notify.error(serverError(error, "Failed to load configuration"));
    } finally {
      setLoading(false);
    }
  }, [configId, form]);

  useEffect(() => {
    if (configId) fetchConfig();
  }, [configId, fetchConfig]);

  const onSave = async (values) => {
    setSaving(true);
    try {
      const payload = EDITABLE_KEYS.reduce((acc, k) => ({ ...acc, [k]: values[k] }), {});
      const updated = await updateConfig(configId, payload);
      if (updated) setConfig(updated);
      notify.success("Configuration saved");
      setDirty(false);
    } catch (error) {
      notify.error(serverError(error, "Failed to save configuration"));
    } finally {
      setSaving(false);
    }
  };

  const onReset = () => {
    if (config) form.setFieldsValue(toForm(config));
    setDirty(false);
  };

  if (loading) {
    return (
      <div style={{ padding: 60, textAlign: "center" }}>
        <Spin size="large" />
      </div>
    );
  }

  if (notFound || !config) {
    return (
      <div>
        <Button type="link" icon={<ArrowLeftOutlined />} onClick={() => navigate("/dashboard/configurations")} style={{ paddingLeft: 0, marginBottom: 12 }}>
          Back to Configurations
        </Button>
        <Empty description="Configuration not found" />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Configuration"
        subtitle={config.title || "Untitled configuration"}
        icon={<SettingOutlined />}
        extra={
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate("/dashboard/configurations")}>
            Back
          </Button>
        }
      />

      <Form
        form={form}
        layout="vertical"
        onFinish={onSave}
        onValuesChange={() => setDirty(true)}
        requiredMark={false}
      >
        <Row gutter={[16, 16]}>
          <Col xs={24} lg={16}>
            <Card title="Settings">
              <Form.Item name="title" label="Title" rules={[{ required: true, message: "Enter a title" }]}>
                <Input placeholder="e.g. Lahore Store — Chiller A" maxLength={120} />
              </Form.Item>

              <Divider orientation="left" plain style={{ marginTop: 4 }}>
                Capture &amp; Upload
              </Divider>
              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="pictureFrequency"
                    label="Picture frequency"
                    extra={<Text type="secondary" style={{ fontSize: 12 }}>{humanizeSeconds(pictureFrequency)}{pictureFrequency === 0 ? "" : " between captures"}. Set 0 for door-trigger mode.</Text>}
                    rules={[{ required: true, message: "Required" }]}
                  >
                    <InputNumber min={0} step={30} style={{ width: "100%" }} addonAfter="sec" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="uploadFrequency"
                    label="Upload frequency"
                    extra={<Text type="secondary" style={{ fontSize: 12 }}>{humanizeSeconds(uploadFrequency)} between uploads.</Text>}
                    rules={[{ required: true, message: "Required" }]}
                  >
                    <InputNumber min={1} step={30} style={{ width: "100%" }} addonAfter="sec" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="resolution"
                    label="Resolution"
                    rules={[{ pattern: /^\d{2,5},\d{2,5}$/, message: "Use width,height e.g. 1920,1080" }]}
                  >
                    <Input placeholder="1920,1080" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item name="rotation" label="Rotation">
                    <Select options={ROTATIONS} />
                  </Form.Item>
                </Col>
              </Row>

              <Divider orientation="left" plain>
                Connectivity &amp; Forwarding
              </Divider>
              <Form.Item
                name="wifi"
                label="WiFi"
                extra={<Text type="secondary" style={{ fontSize: 12 }}>Format: SSID,Password</Text>}
              >
                <Input placeholder="MyNetwork,MyPassword" autoComplete="off" />
              </Form.Item>
              <Form.Item
                name="externalUrl"
                label="External forward URL"
                rules={[{ type: "url", message: "Enter a valid URL", warningOnly: true }]}
                extra={<Text type="secondary" style={{ fontSize: 12 }}>Optional — where captured images/QRs are forwarded.</Text>}
              >
                <Input placeholder="https://example.com/webhook" />
              </Form.Item>
              <Form.Item name="externalUrlHeaders" label="Forward headers">
                <Input.TextArea rows={3} placeholder='Optional — e.g. {"Authorization":"Bearer ..."}' />
              </Form.Item>

              <Space>
                <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={saving} disabled={!dirty}>
                  Save changes
                </Button>
                <Button icon={<ReloadOutlined />} onClick={onReset} disabled={!dirty || saving}>
                  Reset
                </Button>
                {dirty && <Text type="warning" style={{ fontSize: 12 }}>Unsaved changes</Text>}
              </Space>
            </Card>
          </Col>

          <Col xs={24} lg={8}>
            <Card title="Details">
              <Descriptions
                column={1}
                size="small"
                items={[
                  { key: "id", label: "Config ID", children: <Text copyable style={{ fontSize: 12 }}>{config._id}</Text> },
                  { key: "created", label: "Created", children: config.createdAt ? dayjs(config.createdAt).format("YYYY-MM-DD HH:mm") : "—" },
                  { key: "updated", label: "Last updated", children: config.updatedAt ? `${dayjs(config.updatedAt).format("YYYY-MM-DD HH:mm")} (${relativeTime(config.updatedAt)})` : "—" },
                ]}
              />
            </Card>
          </Col>
        </Row>
      </Form>
    </div>
  );
};

export default ConfigDetails;
